import {assert} from '@pexip/utils';

import type {Segmenter} from './types';
import type {RendererOptions} from '../../common/types/render';
import type {ReadWritableStream} from '../../common/types/media';
import type {
    Delegate,
    SegmenterOptions,
    ImageSegmenterOptions,
    SegmentationModelAsset,
} from '../../common/types/segmentation';
import type {
    ProcessorEvents,
    ProcessorOptions,
    ProcessorWorkerEvents as WorkerEvents,
} from '../../common/types/processor';
import type {ExtractMessageEventType} from '../../common/types/utils';
import type {FrameTransformStats} from '../../common/types/messageEvents';
import type {ProcessStatus, RenderingEvents} from '../../common/constants';
import {PROCESS_STATUS, RENDERING_EVENTS} from '../../common/constants';
import {cloneImageRecord} from '../../common/utils';

type WorkerEventTypeNoError = Exclude<
    ExtractMessageEventType<WorkerEvents>,
    'error' | 'debug' | 'event'
>;
type Callback = () => void;

type EventHandlers = Map<RenderingEvents, Set<Callback>>;

interface Options extends Omit<ImageSegmenterOptions, 'delegate'> {
    /**
     * The URL for the worker script.
     */
    workerScriptUrl?: URL;
    /**
     * Request Credentials for the worker.
     *
     * @defaultValue 'same-origin'
     */
    credentials?: RequestCredentials;
    /**
     * @see `SegmentationModelAsset`
     */
    modelAsset: SegmentationModelAsset;

    delegate?: () => Delegate;

    log?: (message: string, meta?: unknown) => void;
    /**
     * Stats callback handler, e.g. benchmask
     */
    stats?: (stats: FrameTransformStats[]) => void;
}

export const createSegmenter = (
    /**
     * A base path to specify the directory the Wasm files should be loaded
     * from. If not provided, it will be loaded from the host's root directory.
     *
     * @see {@link
     * https://ai.google.dev/edge/api/mediapipe/js/tasks-vision.filesetresolver#filesetresolverforvisiontasks | FileResolver}
     */
    basePath: SegmenterOptions['basePath'] = '/',
    {
        workerScriptUrl = new URL(
            '../../workers/mediaWorker.js',
            import.meta.url,
        ),
        credentials = 'same-origin',
        delegate,
        log,
        stats,
        ...options
    }: Options = {modelAsset: {modelName: 'selfie', path: ''}},
): Segmenter => {
    const props: {
        mediaWorker?: Worker;
        status: ProcessStatus;
        output?: OffscreenCanvas | null;
        modelAsset: SegmentationModelAsset;
        eventHandlers: EventHandlers;
    } = {
        status: PROCESS_STATUS.New,
        modelAsset: options.modelAsset,
        eventHandlers: new Map(),
    };

    const initWorker = () => {
        const mediaWorker = new Worker(workerScriptUrl, {
            type: 'classic',
            credentials,
        });

        mediaWorker.addEventListener('error', error => {
            log?.(`${error.message}`, error);
            error.preventDefault();
        });
        mediaWorker.addEventListener(
            'message',
            (event: MessageEvent<WorkerEvents>) => {
                switch (event.data.type) {
                    case 'debug': {
                        log?.(event.data.data.message, event.data.data.context);
                        break;
                    }
                    case 'event': {
                        log?.(event.data.data.type, {
                            message: event.data.data.message,
                            error: event.data.data.error,
                        });
                        for (const cb of props.eventHandlers.get(
                            event.data.data.type,
                        ) ?? []) {
                            cb();
                        }
                        switch (event.data.data.type) {
                            case RENDERING_EVENTS.ProcessorRestarted:
                                break;
                            case RENDERING_EVENTS.ContextRestored:
                                void handleContextRestored();
                                break;
                            default:
                                props.status = PROCESS_STATUS.Error;
                                break;
                        }
                        break;
                    }
                    case 'stats': {
                        stats?.(event.data.data);
                        break;
                    }
                    default: {
                        // Do nothing
                        break;
                    }
                }
            },
        );

        return mediaWorker;
    };

    const handleContextRestored = async () => {
        try {
            props.status = PROCESS_STATUS.Updating;
            await postMsg('updated', {
                type: 'update',
                data: {restart: true},
            });
            props.status = PROCESS_STATUS.Updated;
            for (const cb of props.eventHandlers.get('ContextRestored') ?? []) {
                cb();
            }
        } catch (err) {
            log?.('Failed to restart process', {
                error: err,
            });
            props.status = PROCESS_STATUS.Error;
        }
    };

    function postMsg(
        expectedResponseType: 'processed',
        message: ProcessorEvents,
        transfer?: Transferable[],
    ): Promise<MediaStreamTrack | undefined>;
    function postMsg(
        expectedResponseType: 'opened',
        message: ProcessorEvents,
        transfer?: Transferable[],
    ): Promise<Partial<RendererOptions>>;
    function postMsg(
        expectedResponseType: 'updated',
        message: ProcessorEvents,
        transfer?: Transferable[],
    ): Promise<Partial<RendererOptions>>;
    function postMsg(
        expectedResponseType: 'closed',
        message: ProcessorEvents,
        transfer?: Transferable[],
    ): Promise<undefined>;
    function postMsg(
        expectedResponseType: undefined,
        message: ProcessorEvents,
        transfer?: Transferable[],
    ): void;
    async function postMsg<T extends WorkerEventTypeNoError>(
        expectedResponseType: T | undefined,
        message: ProcessorEvents,
        transfer?: Transferable[],
    ): Promise<Partial<RendererOptions> | MediaStreamTrack | undefined> {
        if (!expectedResponseType) {
            props.mediaWorker?.postMessage(message, transfer ?? []);
            return;
        }
        return await new Promise<MediaStreamTrack | undefined>(
            (resolve, reject) => {
                const handleMessage = (ev: MessageEvent<WorkerEvents>) => {
                    switch (ev.data.type) {
                        case expectedResponseType: {
                            removeListener();
                            // @ts-expect-error --- Call `resolve` is expecting to pass an argument or nothing
                            resolve(ev.data.data);
                            break;
                        }
                        case 'debug':
                        case 'stats': {
                            // Nothing to handle -- these are continues
                            // messages handled by the global event listener
                            break;
                        }
                        case 'event': {
                            removeListener();
                            reject(
                                ev.data.data.error ??
                                    new Error(ev.data.data.type, {
                                        cause: ev.data.data.message,
                                    }),
                            );
                            break;
                        }
                        default: {
                            // Ignore messages that don't match the expected
                            // response type. Other concurrent requests (e.g.
                            // a `close` arriving while a `processed` is in
                            // flight) post their own responses on the same
                            // worker; rejecting here would cross-poison
                            // listeners and surface false errors.
                            break;
                        }
                    }
                };
                const handleError = (error: ErrorEvent) => {
                    removeListener();
                    reject(error.error ?? new Error(error.message));
                };
                const removeListener = () => {
                    props.mediaWorker?.removeEventListener(
                        'message',
                        handleMessage,
                    );
                    props.mediaWorker?.removeEventListener(
                        'error',
                        handleError,
                    );
                };
                props.mediaWorker?.addEventListener('message', handleMessage);
                props.mediaWorker?.addEventListener('error', handleError);
                props.mediaWorker?.postMessage(message, transfer ?? []);
            },
        );
    }

    const open = async ({
        imageSegmenterOptions,
        ...restOptions
    }: ProcessorOptions) => {
        props.status = PROCESS_STATUS.Opening;
        if (!props.mediaWorker) {
            props.mediaWorker = initWorker();
        }

        const backgroundImage =
            restOptions?.backgroundImage &&
            (await cloneImageRecord(restOptions.backgroundImage));

        try {
            const processorSettings = {
                basePath,
                ...restOptions,
                imageSegmenterOptions: {
                    ...options,
                    delegate: delegate?.(),
                    ...imageSegmenterOptions,
                },
                backgroundImage,
            };
            log?.('Init processor with config', {config: processorSettings});
            const result = await postMsg(
                'opened',
                {
                    type: 'open',
                    data: processorSettings,
                },
                backgroundImage?.image ? [backgroundImage.image] : [],
            );
            props.status = PROCESS_STATUS.Opened;
            return result;
        } catch (error: unknown) {
            props.status = PROCESS_STATUS.Error;
            log?.('Failed to open process', {
                error: error,
            });
            return {};
        }
    };

    async function process(
        input: MediaStreamTrack,
        options?: RendererOptions,
    ): Promise<MediaStreamTrack>;
    async function process(
        input: ReadWritableStream<VideoFrame>,
        options?: RendererOptions,
    ): Promise<ReadWritableStream<VideoFrame> | undefined>;
    async function process(
        input: MediaStreamTrack | ReadWritableStream<VideoFrame>,
        options?: RendererOptions,
    ): Promise<MediaStreamTrack | ReadWritableStream<VideoFrame> | undefined> {
        if (
            props.status === PROCESS_STATUS.Error ||
            props.status === PROCESS_STATUS.Updating
        ) {
            return input;
        }
        props.status = PROCESS_STATUS.Processing;
        if (input instanceof MediaStreamTrack) {
            try {
                // Clone the track and keep the original in main
                const clonedTrack = input.clone();
                const output = await postMsg(
                    'processed',
                    {
                        type: 'process',
                        data: {track: clonedTrack, renderOptions: options},
                    },
                    [clonedTrack],
                );
                assert(output);
                props.status = PROCESS_STATUS.Idle;
                return output;
            } catch (error: unknown) {
                props.status = PROCESS_STATUS.Error;
                log?.(
                    'Error during the processing, return original track instead.',
                    {error},
                );
                return input;
            }
        } else {
            try {
                await postMsg(
                    'processed',
                    {
                        type: 'process',
                        data: {
                            readable: input.readable,
                            writable: input.writable,
                            renderOptions: options,
                        },
                    },
                    [input.readable, input.writable],
                );
                props.status = PROCESS_STATUS.Idle;
            } catch (error: unknown) {
                props.status = PROCESS_STATUS.Error;
                log?.(
                    'Error during the processing, return original ReadWritableStream instead.',
                    {
                        error,
                    },
                );
                return input;
            }
        }
    }

    const update: Segmenter['update'] = async options => {
        try {
            if (
                props.status === PROCESS_STATUS.Processing ||
                props.status === PROCESS_STATUS.Opening ||
                props.status === PROCESS_STATUS.Updating
            ) {
                const waitForStatus = new Promise((resolve, reject) => {
                    const handleMessage = (ev: MessageEvent<WorkerEvents>) => {
                        if (
                            ev.data.type === 'processed' ||
                            ev.data.type === 'opened' ||
                            ev.data.type === 'updated' ||
                            ev.data.type === 'closed'
                        ) {
                            props.mediaWorker?.removeEventListener(
                                'message',
                                handleMessage,
                            );
                            resolve(undefined);
                        } else if (
                            ev.data.type !== 'debug' &&
                            ev.data.type !== 'stats'
                        ) {
                            reject(
                                new Error(`UnhandledEvent: ${ev.data.type}`),
                            );
                        }
                    };
                    props.mediaWorker?.addEventListener(
                        'message',
                        handleMessage,
                    );
                });
                await waitForStatus;
            }
            props.status = PROCESS_STATUS.Updating;
            const backgroundImage =
                options.backgroundImage &&
                (await cloneImageRecord(options.backgroundImage));
            log?.('Update processor config', {config: options});
            const transfer = backgroundImage ? [backgroundImage.image] : [];
            const changed = await postMsg(
                'updated',
                {
                    type: 'update',
                    data: {
                        ...options,
                        backgroundImage,
                    },
                },
                transfer,
            );
            props.status = PROCESS_STATUS.Updated;
            return changed;
        } catch (error: unknown) {
            props.status = PROCESS_STATUS.Error;
            log?.('Failed to update process', {
                error: error,
            });
            return {};
        }
    };
    const mute = (muted: boolean) => {
        postMsg(undefined, {type: 'mute', data: muted});
    };
    const close = async () => {
        props.status = PROCESS_STATUS.Closing;
        log?.('Close processor');
        await postMsg('closed', {type: 'close'});
        props.status = PROCESS_STATUS.Closed;
    };
    const destroy = async () => {
        props.status = PROCESS_STATUS.Destroying;
        log?.('Destroy processor');
        props.eventHandlers.clear();
        postMsg(undefined, {type: 'destroy'});
        props.mediaWorker?.terminate();
        props.mediaWorker = undefined;
        props.status = PROCESS_STATUS.Destroyed;
        return Promise.resolve();
    };

    return {
        get modelAsset() {
            return props.modelAsset;
        },
        set modelAsset(asset) {
            props.modelAsset = asset;
        },
        get status() {
            return props.status;
        },
        subscribe: (event, callback) => {
            let handlers = props.eventHandlers.get(event);
            if (!handlers) {
                props.eventHandlers.set(event, new Set());
                handlers = props.eventHandlers.get(event);
            }
            assert(handlers);
            handlers.add(callback);
            return () => handlers?.delete(callback);
        },
        open,
        update,
        process,
        mute,
        close,
        destroy,
    };
};
